import { Button } from '@today/ui-primitives'
import { type ReactElement, useId } from 'react'

/** Copy of {@link SecuritySettingsView}; each omitted key falls back to English. */
export interface SecuritySettingsLabels {
	title: string
	description: string
	setUp: string
	unavailable: string
}

const DEFAULT_LABELS: SecuritySettingsLabels = {
	title: 'Security',
	description: 'Protect your administrator account with a one-time code from an authenticator app.',
	setUp: 'Set up authenticator',
	unavailable: 'Authenticator setup is not available in this environment.',
}

/**
 * Props for {@link SecuritySettingsView}.
 *
 * @property available - False when the stage has no enrolment client: the action is disabled and explained.
 * @property isStarting - True while the app prepares the sign-in redirect.
 * @property onSetUp - Starts the enrolment sign-in.
 */
export interface SecuritySettingsViewProps {
	available: boolean
	isStarting?: boolean
	onSetUp: () => void
	labels?: Partial<SecuritySettingsLabels>
}

/** The Settings "Security" section: the entry point of TOTP enrolment (organism, TO-63). */
export function SecuritySettingsView(props: SecuritySettingsViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...props.labels }
	const titleId = useId()
	const unavailableId = useId()
	return (
		<section
			aria-labelledby={titleId}
			className="flex flex-col gap-4 rounded-ds-lg border border-ds-border bg-ds-surface p-6"
		>
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div className="flex flex-col gap-1">
					<h2 id={titleId} className="text-ds-lg font-semibold text-ds-text">
						{text.title}
					</h2>
					<p className="text-ds-sm text-ds-text-muted">{text.description}</p>
				</div>
				<Button
					onPress={props.onSetUp}
					isDisabled={!props.available || props.isStarting === true}
					aria-describedby={props.available ? undefined : unavailableId}
				>
					{text.setUp}
				</Button>
			</header>
			{props.available ? null : (
				<p id={unavailableId} className="text-ds-sm text-ds-text-muted">
					{text.unavailable}
				</p>
			)}
		</section>
	)
}
